import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';

import { Grid, Icon, IconButton, Tooltip } from '@mui/material';

import {
  FaIcon,
  MDBox,
  MDTypography,
  Notification,
  AuthGuard
} from '@components/index';
import { navbarDesktopMenu } from '@components/Navbars/DashboardNavbar/styles';
import NavbarMenuItem from './NavbarMenuItem';

import { RootState } from '@store/store';
import { setMiniSidenav, useMaterialUIController } from '@context/MDContext';
import { setPlanValidityText } from '@store/AppSlice';

import { sendAmplitudeData } from '@utils/amplitude';
import { getPageName, getUTCTime } from '@utils/common';
import useCustomNavigate from '@hooks/useCustomNavigate';
import breakpoints from '@theme/base/breakpoints';
import { PlanWarnings } from '@constants/pricing';
import { BACK_TO_PROJECT, CLICKED_NOTIFICATION } from '@constants/eventNames';
import { GO_BACK_MESSAGE } from '@constants/toolTips';
import { PageNames } from '@constants/common';

interface NavbarProps {
  pageTitle: string;
  light?: boolean;
  sectionTitle?: string;
  createdAt?: string;
  showBackButton?: boolean;
  sectionTooltip?: string;
  titleTooltip?: string;
  goBackMsg?: string;
  goBackDestination?: string;
  page?: string;
  showUploadLimitMessage?: boolean;
}

interface NavbarItemProps {
  icon: string;
  iconLabel: string;
  onNavbarItemClick?: () => void;
  link?: string;
  needAuthGuard: boolean;
  color?: string;
}
const NavBar: FC<NavbarProps> = ({
  pageTitle,
  light,
  sectionTitle,
  showBackButton,
  sectionTooltip = '',
  titleTooltip = '',
  goBackMsg = '',
  createdAt,
  goBackDestination,
  page,
  showUploadLimitMessage = false
}) => {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, darkMode } = controller;
  const navigate = useCustomNavigate();
  const location: any = useLocation();
  const storeDispatch = useDispatch();

  const userDetails = useSelector((state: RootState) => state?.userDetails);
  const notificationsData = useSelector(
    (state: RootState) => state?.notificationsData
  );
  const projectUploadLimitByPlan = useSelector(
    (state: RootState) => state?.AppData.currentPlanFeatures.projectLimit
  );
  const projectBoard = useSelector((state: RootState) => state?.projectBoard);
  const currentProjectData = useSelector(
    (state: RootState) => state?.projectBoard?.currentProjectData
  );
  const subscriptionFlag = useSelector(
    (state: RootState) => state?.AppData?.subKeyFlag
  );
  const currentPlanFeatures = useSelector(
    (state: RootState) => state?.AppData.currentPlanFeatures
  );

  const unreadMsgCount = notificationsData.unreadMessages?.length;
  const navbarMenuItems: NavbarItemProps[] = [
    {
      icon: 'bell',
      iconLabel: 'MSG',
      needAuthGuard: false,
      color: 'info',
      onNavbarItemClick: () => {
        handleOpenMenu();
        sendAmplitudeData(CLICKED_NOTIFICATION);
      }
    }
  ];
  const pageName = getPageName();
  const projectRemarks = projectBoard?.currentProjectData?.project_remark;

  const [autoSaveTime, setAutoSaveTime] = useState<string>();
  const [openMenu, setOpenMenu] = useState<boolean>(false);
  const [dataUpdatedAt, setDataUpdatedAt] = useState('');
  const anchorEl = useRef();

  useEffect(() => {
    if (createdAt) {
      const interval = setInterval(() => {
        setAutoSaveTime(dayjs(createdAt).fromNow());
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [createdAt]);

  useEffect(() => {
    const excludedPages = [
      PageNames.MY_PROJECTS,
      PageNames.DELETED_PROJECTS,
      PageNames.EDIT_CHART
    ];

    if (
      !excludedPages.includes(pageName as PageNames) &&
      currentProjectData?.project_data_updated_at &&
      (currentProjectData?.is_api_connected ||
        currentProjectData?.project_datafiles?.[0]?.filetype === 'GSHEET')
    )
      setDataUpdatedAt(getUTCTime(currentProjectData?.project_data_updated_at));
    else setDataUpdatedAt('');
  }, [pageName, currentProjectData?.project_data_updated_at]);

  const [mobileView, setMobileView] = useState<boolean>(false);

  useEffect(() => {
    // A function that sets the display state for the DefaultNavbarMobile.
    function displayMobileNavbar() {
      if (window.innerWidth < breakpoints.values.lg) setMobileView(true);
      else setMobileView(false);
    }

    /** The event listener that's calling the displayMobileNavbar function when resizing the window.
     */
    window.addEventListener('resize', displayMobileNavbar);

    // Call the displayMobileNavbar function to set the state with the initial value.
    displayMobileNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', displayMobileNavbar);
  }, []);

  const limitExceededFeatures = useMemo(() => {
    const featureList: string[] = [];

    const projectUploadLimit = currentPlanFeatures?.projectLimit;
    const squirbyLimit = currentPlanFeatures?.squirbyLimit;
    const insightsLimit = currentPlanFeatures?.insightsLimit;
    const gSheetUpdateLimit = currentPlanFeatures?.gSheetUpdateLimit;
    const apiUpdateLimit = currentPlanFeatures?.apiUpdateLimit;
    const apiUploadLimit = currentPlanFeatures?.apiUploadLimit;

    if (
      projectUploadLimit &&
      (!isNaN(userDetails?.project_monthly_limit_remaining as number)
        ? userDetails?.project_monthly_limit_remaining
        : projectUploadLimit - userDetails?.project_count || 0) === 0
    )
      featureList.push('Uploading Data');

    if (squirbyLimit && (userDetails?.squirby_limit_remaining || 0) === 0)
      featureList.push('Squirby Interactions');

    if (insightsLimit && (userDetails?.insights_limit_remaining || 0) === 0)
      featureList.push('AI Insights');

    if (gSheetUpdateLimit && (userDetails?.data_updates_remaining || 0) === 0)
      featureList.push('Updating Google Sheets');

    if (apiUpdateLimit && (userDetails?.api_updates_remaining || 0) === 0)
      featureList.push('API Recalculations');

    if (apiUploadLimit && (userDetails?.api_uploads_remaining || 0) === 0)
      featureList.push('API Updates');

    return featureList;
  }, [userDetails, currentPlanFeatures]);

  useEffect(() => {
    if (userDetails?.plan && subscriptionFlag)
      if (limitExceededFeatures.length)
        storeDispatch(
          setPlanValidityText({
            type: PlanWarnings.FEATURE_LIMIT_EXCEEDED,
            value: `You’ve exceeded the maximum limit for ${limitExceededFeatures.join(
              ', '
            )} under your current plan. `
          })
        );
      // if (isInRange)
      //   storeDispatch(
      //     setPlanValidityText({
      //       type: PlanWarnings.LAUNCH_MESSAGE,
      //       value: `We're live on Product Hunt! Your vote today would mean the world to us. Support us `
      //     })
      //   );
      // if (
      //   userDetails?.expired_plan &&
      //   userDetails?.plan === PricingPlans.NO_ACTIVE_SUBSCRIPTION &&
      //   userDetails?.expired_plan_is_trial
      //   // && remainingDays <= 0 && remainingDays >= -6
      // )
      //   storeDispatch(
      //     setPlanValidityText({
      //       type: PlanWarnings.EXPIRED_PLAN,
      //       value: `Your ${getPlanName(
      //         userDetails.expired_plan,
      //         userDetails.expired_plan_is_trial
      //       )} plan has expired. To continue using your most-loved features, `
      //     })
      //   );
      // else if (
      //   remainingMilliseconds <=
      //     Number(`${process.env.REACT_APP_BANNER_TRIAL_PLAN_EXPIRY_DAYS}`) *
      //       86400000 &&
      //   userDetails?.is_trial
      // )
      //   storeDispatch(
      //     setPlanValidityText({
      //       type: PlanWarnings.UPGRADE_TRIAL,
      //       value: `${
      //         remainingDays === 1
      //           ? `${remainingDays} day`
      //           : `${remainingDays} days`
      //       } left for your ${getPlanName(
      //         userDetails.plan,
      //         userDetails.is_trial
      //       )} plan to end. To continue using your most-loved features, `
      //     })
      //   );
      else
        storeDispatch(
          setPlanValidityText({ type: PlanWarnings.NONE, value: '' })
        );
  }, [
    subscriptionFlag,
    userDetails?.plan,
    userDetails?.plan_end_date,
    userDetails?.project_count,
    projectUploadLimitByPlan,
    limitExceededFeatures
  ]);

  const handleCloseMenu = () => setOpenMenu(false);
  const handleOpenMenu = () => setOpenMenu(true);
  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const iconsStyle = ({
    palette: { dark, white, text },
    functions: { rgba }
  }: {
    palette: any;
    functions: any;
  }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light)
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;

      return colorValue;
    }
  });

  return (
    <>
      <Grid
        container
        justifyContent='space-between'
        alignItems='center'
        pt='0.5px'
      >
        <Grid item>
          <Grid container spacing={2} alignItems='center' justifyItems='center'>
            {mobileView && (
              <Grid item>
                <IconButton
                  sx={navbarDesktopMenu}
                  onClick={handleMiniSidenav}
                  size='small'
                  disableRipple
                >
                  <Icon fontSize='medium' sx={iconsStyle}>
                    {miniSidenav ? 'menu_open' : 'menu'}
                  </Icon>
                </IconButton>
              </Grid>
            )}

            {showBackButton && (
              <Grid item sx={{ paddingTop: '20px !important' }}>
                <Tooltip title={goBackMsg} placement='top'>
                  <IconButton
                    sx={iconsStyle}
                    onClick={() => {
                      if (goBackMsg === GO_BACK_MESSAGE)
                        sendAmplitudeData(BACK_TO_PROJECT);
                      goBackDestination
                        ? navigate(goBackDestination)
                        : navigate(-1);
                    }}
                  >
                    <FaIcon
                      typographyProps={{ variant: 'md', color: 'info' }}
                      icon={['far', 'left']}
                    />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
            {sectionTitle && (
              <Grid item>
                <MDBox>
                  <Tooltip title={sectionTooltip} placement='bottom'>
                    <MDTypography
                      variant='sm'
                      color='info'
                      sx={{
                        textTransform: 'uppercase',

                        fontWeight: '800'
                      }}
                    >
                      {sectionTitle}
                    </MDTypography>
                  </Tooltip>
                </MDBox>
              </Grid>
            )}
            {pageTitle && (
              <Grid item>
                <Tooltip title={titleTooltip} placement='bottom'>
                  <MDTypography variant='sm' color='info' fontWeight='regular'>
                    {pageTitle}
                  </MDTypography>
                </Tooltip>
              </Grid>
            )}
            {createdAt && (
              <Grid item display='flex' alignItems='flex-end' columnGap={1}>
                <Tooltip
                  title={
                    location.state?.state?.fromAddChartFlag
                      ? `This ${
                          sectionTitle === 'KPI' ? 'KPI' : 'chart'
                        } is automatically saved to your Project’s Board. If you wish to delete it, select Go Back arrow & delete from Board.`
                      : 'Changes are auto saved'
                  }
                >
                  <MDTypography variant='sm' color='info' fontWeight='400'>
                    (Auto-saved {autoSaveTime})
                  </MDTypography>
                </Tooltip>
              </Grid>
            )}
            {dataUpdatedAt && (
              <Grid item display='flex' alignItems='flex-end' columnGap={1}>
                <MDTypography variant='sm' color='info' fontWeight='400'>
                  (Data last updated on {dataUpdatedAt})
                </MDTypography>
              </Grid>
            )}
            {projectRemarks && showUploadLimitMessage && (
              <Grid item>
                <Tooltip
                  title="This project is created under a plan with limits on the number of rows and columns that can be uploaded. If you haven't already, upgrade your plan by selecting the Upgrade button in the left panel. You will need to upload or connect the file again to import your data in full."
                  placement='right'
                >
                  <MDBox
                    sx={{
                      background: 'linear-gradient(310deg, #17375F, #236DA9)',
                      textTransform: 'capitalize',
                      cursor: 'default',
                      fontSize: '12px',
                      padding: '5px 15px',
                      borderRadius: '8px'
                    }}
                  >
                    <span style={{ color: '#FFFFFF' }}>
                      {projectRemarks + '. '}
                      {/* {userDetails?.plan ===
                        PricingPlans.NO_ACTIVE_SUBSCRIPTION && (
                        <>
                          <Link
                            sx={{
                              textDecoration: 'underline',
                              textUnderlineOffset: '3px',
                              textTransform: 'none',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              storeDispatch(
                                setPlanUpdateErrors({
                                  button_text: userDetails?.has_used_trial
                                    ? 'Upgrade'
                                    : 'Start your Free 3 day Trial',
                                  feature_name: 'Label Click',
                                  modal_name: getModalName(
                                    PlanWarnings.LABEL_CLICK
                                  ),
                                  title: getPlanValidityTitle(
                                    PlanWarnings.LABEL_CLICK,
                                    (projectUploadLimitByPlan as number) >
                                      userDetails?.project_count
                                      ? (projectUploadLimitByPlan as number) -
                                          userDetails?.project_count
                                      : 0,
                                    projectUploadLimitByPlan as number,
                                    userDetails?.plan,
                                    userDetails?.is_trial,
                                    userDetails?.plan_end_date
                                  ),
                                  sub_title: getPlanValiditySubTitle(
                                    PlanWarnings.LABEL_CLICK,
                                    userDetails?.has_used_trial,
                                    paymentModalMessage
                                  )
                                })
                              );
                              sendAmplitudeData(SHOWED_PAYMENT_REQUIRED, {
                                current_plan:
                                  PricingPlans.NO_ACTIVE_SUBSCRIPTION,
                                project_id: projectId
                                  ? projectBoard?.currentProjectData?.id
                                  : '',
                                feature_name: 'Label Click',
                                modal_name: 'Free plan',
                                is_trial_utilised: userDetails?.has_used_trial
                                  ? 'yes'
                                  : 'no'
                              });
                            }}
                          >
                            Click here
                          </Link>{' '}
                          <span style={{ textTransform: 'none' }}>
                            to know why
                          </span>
                        </>
                      )} */}
                    </span>
                  </MDBox>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item display={{ sm: 'block', xs: 'none' }}>
          <Grid container pr={page === 'EditChart' ? 2 : 0}>
            {navbarMenuItems.map(
              ({
                iconLabel,
                icon,
                onNavbarItemClick,
                needAuthGuard,
                link,
                color
              }: NavbarItemProps) => (
                <AuthGuard
                  is_demo_user={userDetails.user.is_demo_user}
                  needAuthGuard={needAuthGuard}
                  key={iconLabel}
                >
                  <Grid item pl={0} ref={anchorEl.current}>
                    <NavbarMenuItem
                      icon={icon}
                      iconLabel={iconLabel}
                      onNavbarItemClick={() =>
                        onNavbarItemClick && onNavbarItemClick()
                      }
                      messageCount={unreadMsgCount}
                      islink={link ? link : false}
                      color={color}
                      toolTipText={
                        iconLabel === 'LOGOUT' ? userDetails.user.email : ''
                      }
                    />
                  </Grid>
                </AuthGuard>
              )
            )}
          </Grid>
          <Notification
            anchorEl={anchorEl}
            handleCloseMenu={handleCloseMenu}
            openMenu={openMenu}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default NavBar;
